import { useState, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import {
  DataTable,
  DataTableColumn,
  FilterField,
} from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import InlineFilter from '../../components/ui/inline-filter';
import PageHeader from '../../components/common/PageHeader';
import StatsCard from '../../components/common/StatsCard';
import ActionDropdown from '../../components/common/action-dropdown';
import { getStatusColor } from '../../utils/helpers';
import {
  Download,
  Filter,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';

interface Commission {
  id: string;
  clientName: string;
  commissionType: 'lot-based' | 'revenue-share';
  amount: string;
  status: 'pending' | 'paid';
  plan: string;
  tradeDate: string;
  paidDate?: string;
  assignedIB: string;
}

const IBCommissions = () => {
  const [searchValue, setSearchValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const commissions: Commission[] = [
    {
      id: '1',
      clientName: 'John Smith',
      commissionType: 'lot-based',
      amount: '$125.50',
      status: 'paid',
      plan: 'Gold Plan',
      tradeDate: '2024-03-15',
      paidDate: '2024-03-16',
      assignedIB: 'John Anderson',
    },
    {
      id: '2',
      clientName: 'Sarah Johnson',
      commissionType: 'revenue-share',
      amount: '$340.00',
      status: 'paid',
      plan: 'Silver Plan',
      tradeDate: '2024-03-14',
      paidDate: '2024-03-15',
      assignedIB: 'John Anderson',
    },
    {
      id: '3',
      clientName: 'Mike Williams',
      commissionType: 'lot-based',
      amount: '$85.25',
      status: 'pending',
      plan: 'Bronze Plan',
      tradeDate: '2024-03-18',
      assignedIB: 'Michael Chen',
    },
    {
      id: '4',
      clientName: 'Emily Davis',
      commissionType: 'revenue-share',
      amount: '$520.75',
      status: 'paid',
      plan: 'Platinum Plan',
      tradeDate: '2024-03-13',
      paidDate: '2024-03-14',
      assignedIB: 'Michael Chen',
    },
    {
      id: '5',
      clientName: 'Robert Brown',
      commissionType: 'lot-based',
      amount: '$175.00',
      status: 'pending',
      plan: 'Gold Plan',
      tradeDate: '2024-03-19',
      assignedIB: 'John Anderson',
    },
    {
      id: '6',
      clientName: 'Jennifer Lee',
      commissionType: 'revenue-share',
      amount: '$290.50',
      status: 'paid',
      plan: 'Silver Plan',
      tradeDate: '2024-03-12',
      paidDate: '2024-03-13',
      assignedIB: 'Sarah Mitchell',
    },
    {
      id: '7',
      clientName: 'David Wilson',
      commissionType: 'lot-based',
      amount: '$45.00',
      status: 'pending',
      plan: 'Bronze Plan',
      tradeDate: '2024-03-20',
      assignedIB: 'Lisa Wang',
    },
    {
      id: '8',
      clientName: 'Amanda Taylor',
      commissionType: 'revenue-share',
      amount: '$680.00',
      status: 'paid',
      plan: 'Platinum Plan',
      tradeDate: '2024-03-11',
      paidDate: '2024-03-12',
      assignedIB: 'Michael Chen',
    },
  ];

  const filterFields: FilterField[] = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
      ],
    },
    {
      id: 'commissionType',
      label: 'Commission Type',
      options: [
        { value: 'lot-based', label: 'Lot-based' },
        { value: 'revenue-share', label: 'Revenue Share' },
      ],
    },
  ];

  const filteredCommissions = useMemo(() => {
    return commissions.filter(commission => {
      if (activeFilters.status && commission.status !== activeFilters.status) {
        return false;
      }
      if (
        activeFilters.commissionType &&
        commission.commissionType !== activeFilters.commissionType
      ) {
        return false;
      }
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        return (
          commission.clientName.toLowerCase().includes(searchLower) ||
          commission.plan.toLowerCase().includes(searchLower) ||
          commission.assignedIB.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [commissions, activeFilters, searchValue]);

  const columns: DataTableColumn<Commission>[] = [
    {
      id: 'clientName',
      header: 'Client Name',
      accessor: row => row.clientName,
      sortable: true,
      sortValue: row => row.clientName,
      width: '120px',
    },
    {
      id: 'commissionType',
      header: 'Commission Type',
      accessor: row => (
        <Badge className={getStatusColor(row.commissionType)}>
          {row.commissionType}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.commissionType,
      width: '150px',
    },
    {
      id: 'plan',
      header: 'Commission Plan',
      accessor: row => row.plan,
      sortable: true,
      sortValue: row => row.plan,
      width: '160px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => row.amount,
      sortable: true,
      sortValue: row => row.amount,
      width: '120px',
    },
    {
      id: 'assignedIB',
      header: 'Assigned IB',
      accessor: row => row.assignedIB,
      sortable: true,
      sortValue: row => row.assignedIB,
      width: '150px',
    },
    {
      id: 'tradeDate',
      header: 'Trade Date',
      accessor: row => row.tradeDate,
      sortable: true,
      sortValue: row => row.tradeDate,
      width: '140px',
    },
    {
      id: 'paidDate',
      header: 'Paid Date',
      accessor: row => row.paidDate || '-',
      sortable: true,
      sortValue: row => row.paidDate || '',
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge className={getStatusColor(row.status)}>{row.status}</Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          size="sm"
          variant="ghost"
          showView={true}
          showEdit={true}
          showDelete={false}
          showDeposit={false}
          showWithdraw={false}
          onView={() => console.log('View commission:', row.id)}
          onEdit={() => console.log('Edit commission:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="IB Commissions"
          description="Manage and monitor commission structures"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Commission"
          value="$0.00"
          change="+0% from last month"
          icon={DollarSign}
        />
        <StatsCard
          title="Pending Commission"
          value="$0.00"
          change="+0% from last month"
          icon={Clock}
        />
        <StatsCard
          title="Paid Commission"
          value="$0.00"
          change="+0% from last month"
          icon={CheckCircle2}
        />
        <StatsCard
          title="Total Trades"
          value="0"
          change="+0% from last month"
          icon={TrendingUp}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <CardTitle>Commission History</CardTitle>
              <CardDescription>
                View and manage all commission records
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filter' : 'Show Filter'}
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <InlineFilter
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
            onFiltersClear={() => setActiveFilters({})}
            onFiltersApply={() => {}}
            title="Filter Commissions"
          />
          <DataTable
            columns={columns}
            data={filteredCommissions}
            searchValue={searchValue}
            searchPlaceholder="Search commissions..."
            onSearchChange={setSearchValue}
            onSearchReset={() => setSearchValue('')}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFiltersChange={() => {}}
            showColumnVisibility={true}
            showAddButton={false}
            showExportButton={false}
            initialPageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default IBCommissions;
